import { FC, useMemo, useCallback, useEffect, useState, ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { DefinedUseQueryResult } from '@tanstack/react-query'

import { Button, CrudModal, SafetyCaseHazardForm } from '../../../components'
import { useDeleteSafetyCaseHazardMutation } from '../../../services'

import { useModal } from '../../../hooks'

import { IRefs, ISafetyCase, ISafetyCaseHazard } from '../../../interfaces'
import SafetyCaseHazardCardsList from './safety-case-hazard-cards-list'

type SafetyCaseHazardsProps = {
  hasWriteAccess?: boolean
  safetyCaseQuery: DefinedUseQueryResult<ISafetyCase | undefined, Error>
  setHeaderControls: (headerControls?: ReactElement) => void
  hazardsRefs: IRefs
  hazardsPagination: {
    pageSize: number
    pageIndex: number
  }
  setHazardsPagination: (values: { [key: string]: number }) => void
  activeHazardId?: number
}

const SafetyCaseHazards: FC<SafetyCaseHazardsProps> = props => {
  const {
    hasWriteAccess,
    safetyCaseQuery,
    setHeaderControls,
    hazardsRefs,
    hazardsPagination,
    setHazardsPagination,
    activeHazardId,
  } = props

  const { data: safetyCase, isFetching: isSafetyCaseLoading, refetch: refetchSafetyCase } = safetyCaseQuery

  const hazards = safetyCase?.hazards || []

  const {
    isOpen: isHazardDeleteModalOpen,
    closeModal: closeHazardDeleteModal,
    openModal: openHazardDeleteModal,
  } = useModal()

  const navigate = useNavigate()

  const handleNavigateToCreateHazard = useCallback(() => navigate('new'), [navigate])

  const [deletingHazard, setDeletingHazard] = useState<ISafetyCaseHazard>()

  const handleOpenDeleteHazardModal = useCallback(
    (hazard: ISafetyCaseHazard) => {
      setDeletingHazard(hazard)
      openHazardDeleteModal()
    },
    [openHazardDeleteModal],
  )

  const headerControls = useMemo(
    () => <Button onClick={handleNavigateToCreateHazard}>Create Hazard</Button>,
    [handleNavigateToCreateHazard],
  )

  useEffect(() => {
    setHeaderControls(headerControls)
    return () => setHeaderControls(undefined)
  }, [setHeaderControls, headerControls])

  const handleOpenHazard = useCallback(
    (hazard: ISafetyCaseHazard) => {
      if (!safetyCase?.id || hazard.id === undefined || hazard.id === null) {
        return
      }

      navigate(`${hazard.id}`)
    },
    [navigate, safetyCase?.id],
  )

  const hazardActionsButtonOptions = useMemo(
    () =>
      hasWriteAccess
        ? [
            {
              label: 'View/Update',
              handler: (hazard: ISafetyCaseHazard) => handleOpenHazard(hazard),
            },
            {
              label: 'Delete',
              handler: (hazard: ISafetyCaseHazard) => handleOpenDeleteHazardModal(hazard),
            },
          ]
        : undefined,
    [hasWriteAccess, handleOpenHazard, handleOpenDeleteHazardModal],
  )
  return (
    <>
      <CrudModal
        modalSettings={{
          isOpen: isHazardDeleteModalOpen,
          closeModal: closeHazardDeleteModal,
          mode: isHazardDeleteModalOpen ? 'delete' : undefined,
        }}
        deleteSettings={{
          title: 'Delete Safety Hazard',
          useDeleteMutation: useDeleteSafetyCaseHazardMutation,
          onDeleteSuccessMessage: 'Hazard successfully deleted',
          onDeleteErrorMessage: 'Something went wrong while deleting hazard',
          onSuccessCallback: () => {
            refetchSafetyCase()
          },
          deletingItem: deletingHazard,
        }}
        FormComponent={SafetyCaseHazardForm}
        formName="safety-case-hazard"
        mutators={undefined}
      />

      <SafetyCaseHazardCardsList
        hazards={hazards}
        pagination={hazardsPagination}
        setPagination={setHazardsPagination}
        actionsButtonOptions={hazardActionsButtonOptions}
        hazardsRefs={hazardsRefs}
        activeHazardId={activeHazardId}
        isLoading={isSafetyCaseLoading}
      />
    </>
  )
}

export default SafetyCaseHazards
